/**
 * env.ts
 *
 * ACP transport configuration. Follows the same base64-encoded cert/creds
 * pattern as AuthorizerConfig in lib/middleware — all secrets arrive as
 * env vars, never as file paths.
 */

// ─── HTTP Auth ────────────────────────────────────────────────────────────────
// Shared auth config for all HTTP-based transports (WS, SSE, Webhook REST).
// Discriminated by `type` — no defaults, caller must supply the correct variant.

export type HttpAuth =
  /** Standard Bearer token — Authorization: Bearer {token} */
  | { type: "bearer"; token: string }
  /** Arbitrary API key header — e.g. X-Api-Key, X-PineSeed-Key */
  | { type: "api_key"; header: string; key: string }
  /** HTTP Basic auth — Authorization: Basic base64({username}:{password}) */
  | { type: "basic"; username: string; password: string };

/**
 * Resolve an HttpAuth value to a single Authorization (or custom) header entry.
 * Returns a record with exactly one header key/value pair.
 */
export function resolveAuthHeaders(auth: HttpAuth): Record<string, string> {
  switch (auth.type) {
    case "bearer":
      return { authorization: `Bearer ${auth.token}` };
    case "api_key":
      return { [auth.header]: auth.key };
    case "basic": {
      const encoded = btoa(`${auth.username}:${auth.password}`);
      return { authorization: `Basic ${encoded}` };
    }
  }
}
